import React, { useState } from "react";
import toast from "react-hot-toast";
import { selectActiveClub, listCoachClubs } from "../../services/coachClubService";
import RegisterAdditionalClubModal from "./RegisterAdditionalClubModal";

const PRIMARY = "#1d4ed8";
const NAVY = "#0f1c3f";
const LIGHT = "#e8f0fe";

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("active") || s.includes("paid") || s.includes("approved")) return { bg: "#dcfce7", color: "#15803d" };
  if (s.includes("pending") || s.includes("await")) return { bg: "#fef3c7", color: "#b45309" };
  if (s.includes("reject") || s.includes("expired") || s.includes("fail")) return { bg: "#fee2e2", color: "#b91c1c" };
  return { bg: "#e5e7eb", color: "#4b5563" };
};

const Pill = ({ children }) => {
  const c = statusColor(children);
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: c.bg, color: c.color }}>
      {children || "—"}
    </span>
  );
};

// "Select a Club" popup — shown when a coach's /verify-otp response carries
// `redirect_to: "club_select"` (2+ clubs, see ClubCoachMembership::redirectForClubs
// on the backend). Shared by verifyRouting.js's two callers: HomePage.jsx's
// LoginCard renders it as an in-page modal (portalled to document.body,
// same z-[60] layering as its other popups) via routeAfterVerify's
// `onClubSelect` callback, and ClubSelectPage.jsx (the standalone /select-club
// route, still used by callers that don't pass onClubSelect) wraps it full-screen.
//
// Picking a club calls the same POST /api/coach/active-club used by
// ClubSwitcherModal.jsx's in-dashboard switcher (coachClubService.js), then
// enters the dashboard with that club active. "Add Account" nests the same
// RegisterAdditionalClubModal ClubSwitcherModal offers, raised to z-[70] so it
// paints above this popup's own z-[60] overlay.
export default function ClubSelectModal({ member, clubs: initialClubs = [], navigate }) {
  const [clubs, setClubs] = useState(initialClubs);
  const [enteringId, setEnteringId] = useState(null);
  // "Add Account" opens the same "Register Another Club" flow the in-dashboard
  // ClubSwitcherModal offers — the coach is already authenticated at this point
  // (verifyOtp stores the member bearer token before this popup ever renders),
  // so RegisterAdditionalClubModal's member-token-gated calls work here too.
  const [addingClub, setAddingClub] = useState(false);

  // Once a newly registered club is paid, refetch this coach's club list so it
  // shows up here alongside the ones from the original /verify-otp response.
  const handleClubRegistered = () => {
    listCoachClubs()
      .then((data) => setClubs(data?.clubs || []))
      .catch(() => {});
  };

  const handleSelect = async (clubId) => {
    if (enteringId != null) return;
    setEnteringId(clubId);
    try {
      await selectActiveClub(clubId);
      navigate("/coach-dashboard", { state: { member: { ...member, club_id: clubId } }, replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not enter that club. Please try again.");
      setEnteringId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-lg font-bold pr-8" style={{ color: NAVY }}>Select a Club</h2>
        <p className="text-sm text-gray-500 mb-5">You coach more than one club — choose which one to manage.</p>

        <div className="space-y-2">
          {clubs.map((c) => (
            <button
              key={c.id}
              onClick={() => handleSelect(c.id)}
              disabled={enteringId != null}
              className="w-full flex items-center gap-3 rounded-xl border-2 border-gray-200 hover:border-blue-600 hover:bg-blue-50/40 px-4 py-3 transition text-left disabled:opacity-60"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: LIGHT }}>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={PRIMARY}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h.01M11 7h.01M15 7h.01M7 11h.01M11 11h.01M15 11h.01M7 15h.01M11 15h.01M15 15h.01" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold truncate" style={{ color: NAVY }}>{c.club_name}</p>
                  {c.is_primary && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">Primary</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-gray-400">{c.club_code}</span>
                  <Pill>{c.club_status}</Pill>
                </div>
              </div>
              <span className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0" style={{ color: PRIMARY, backgroundColor: LIGHT }}>
                {enteringId === c.id ? "Entering…" : "Enter →"}
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={() => setAddingClub(true)}
          className="w-full mt-5 flex items-center justify-center gap-2 border-2 font-semibold py-2.5 rounded-xl text-sm transition"
          style={{ borderColor: PRIMARY, color: PRIMARY }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Account
        </button>

        <div className="mt-5 pt-5 border-t border-gray-100 text-center">
          <button onClick={() => navigate("/login", { replace: true })} className="text-sm text-gray-500 hover:text-blue-600 font-semibold transition">
            ← Back to Login
          </button>
        </div>
      </div>

      {addingClub && (
        <RegisterAdditionalClubModal
          onClose={() => setAddingClub(false)}
          onRegistered={handleClubRegistered}
          zIndexClass="z-[70]"
        />
      )}
    </div>
  );
}
